<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import draggable from 'vuedraggable';
import { defineStorageStore, RemarkItem } from '@/utils/storage-helper';
import type { TRemarkTag } from '@/types/remarks.type';
import { RemarkTag as RemarkTagIcon, Add } from '@xpos/icons-svg';

type ModelType = 'invoice_line_remark' | 'invoice_remark' | 'void_reason';
const props = defineProps<{ model?: ModelType }>();
const emit = defineEmits<{ 'quick-select': [text: string] }>();

const remarkStores = {
    invoice_line_remark: defineStorageStore<RemarkItem>('invoice_line_remark')(),
    invoice_remark: defineStorageStore<RemarkItem>('invoice_remark')(),
    void_reason: defineStorageStore<RemarkItem>('void_reason')(),
};
const tagStores = {
    invoice_line_remark: defineStorageStore<TRemarkTag>('line_remark_tag')(),
    invoice_remark: defineStorageStore<TRemarkTag>('invoice_remark_tag')(),
};
const store = computed(() => remarkStores[props.model || 'invoice_line_remark']);
const tagStore = computed(() => tagStores[props.model as keyof typeof tagStores] ?? null);

const containerRef = ref<HTMLElement | null>(null);
const editMode = ref(false);
const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const preventClick = ref(false);
const addingTag = ref(false);
const newTagName = ref('');
const activeTag = ref<string | null>(null);
const editingItem = ref<{ type: 'tag' | 'remark'; index: number; value: string; original: RemarkItem } | null>(null);

// Fixed order display - no usage_count sorting
const displayRemarks = computed(() => {
    const all = store.value.data as RemarkItem[];
    return activeTag.value ? all.filter(r => r.name.includes(activeTag.value!)) : all;
});
const tags = computed(() => tagStore.value?.data?.slice(0, 8) ?? []);

// Refs for drag-drop - synced with computed
const draggableRemarks = ref<RemarkItem[]>([]);
const draggableTags = ref<TRemarkTag[]>([]);
watch(displayRemarks, v => { draggableRemarks.value = [...v]; }, { immediate: true });
watch(tags, v => { draggableTags.value = [...v]; }, { immediate: true });

const TAG_COLORS = ['#EA5996', '#80BA5C', '#64B7E6', '#FFB800', '#9877DD', '#37CDC3', '#FF6128', '#9CD5C4'];
const tagColor = (name: string) => TAG_COLORS[name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_COLORS.length];

const isEditing = (type: 'tag' | 'remark', index: number) =>
    editingItem.value?.type === type && editingItem.value.index === index;

const startLongPress = () => {
    longPressTimer.value = setTimeout(() => { editMode.value = true; preventClick.value = true; }, 3000);
};
const cancelLongPress = () => {
    if (longPressTimer.value) { clearTimeout(longPressTimer.value); longPressTimer.value = null; }
};

const handleTagClick = (i: number, tag: TRemarkTag) => {
    if (preventClick.value) { preventClick.value = false; return; }
    if (editMode.value) { editingItem.value = { type: 'tag', index: i, value: tag.name, original: tag as RemarkItem }; return; }
    activeTag.value = activeTag.value === tag.name ? null : tag.name;
};
const handleRemarkClick = (i: number, remark: RemarkItem) => {
    if (preventClick.value) { preventClick.value = false; return; }
    if (editMode.value) { editingItem.value = { type: 'remark', index: i, value: remark.name, original: remark }; return; }
    emit('quick-select', remark.name);
};

// Save new order after drag
const onRemarkDragEnd = async () => {
    const all = store.value.data as RemarkItem[];
    if (activeTag.value) {
        const displayedUids = new Set(draggableRemarks.value.map(r => r.uid));
        const others = all.filter(r => !displayedUids.has(r.uid));
        await store.value.addAll([...draggableRemarks.value, ...others]);
    } else {
        await store.value.addAll([...draggableRemarks.value]);
    }
};
const onTagDragEnd = async () => {
    const allTags = tagStore.value?.data as TRemarkTag[] ?? [];
    const visibleUids = new Set(draggableTags.value.map(t => t.uid));
    const hidden = allTags.filter(t => !visibleUids.has(t.uid));
    await tagStore.value?.addAll([...draggableTags.value, ...hidden]);
};

const saveEdit = async () => {
    if (!editingItem.value) return;
    const { type, value, original } = editingItem.value;
    editingItem.value = null;
    const newName = value.trim();
    if (!newName || newName === original.name) return;
    const s = (type === 'tag' ? tagStore.value! : store.value) as typeof store.value;
    const all = [...s.data] as RemarkItem[];
    const idx = all.findIndex(x => x.uid === original.uid);
    if (idx >= 0) { all[idx] = { ...original, name: newName }; await s.addAll(all); }
};
const cancelEdit = () => { editingItem.value = null; };

const confirmAddTag = async () => {
    const name = newTagName.value.trim();
    if (name) await tagStore.value?.saveNames([name]);
    cancelAddTag();
};
const cancelAddTag = () => { addingTag.value = false; newTagName.value = ''; };

const deleteTag = async (tag: TRemarkTag) => {
    if (activeTag.value === tag.name) activeTag.value = null;
    await tagStore.value?.deleteOne(tag as RemarkItem);
};
const deleteRemark = async (remark: RemarkItem) => { await store.value.deleteOne(remark); };

const handleOutsideClick = (e: MouseEvent) => {
    if (editMode.value && containerRef.value && !containerRef.value.contains(e.target as Node)) {
        editMode.value = false;
        editingItem.value = null;
        cancelAddTag();
    }
};

onMounted(async () => {
    document.addEventListener('click', handleOutsideClick); // Register before async ops to avoid race with onUnmounted
    await store.value.getAll(); store.value.startLiveQuery();
    if (tagStore.value) { await tagStore.value.getAll(); tagStore.value.startLiveQuery(); }
});
onUnmounted(() => {
    store.value.stopLiveQuery();
    tagStore.value?.stopLiveQuery();
    document.removeEventListener('click', handleOutsideClick);
    cancelLongPress();
});
</script>

<template>
    <div ref="containerRef" class="mt-2" :class="{ 'edit-mode': editMode }"
        @pointerdown="startLongPress" @pointerup="cancelLongPress" @pointerleave="cancelLongPress">
        <div v-if="tagStore && (draggableTags.length || editMode)" class="tag-row flex flex-wrap justify-center gap-1 pb-2">
            <draggable :list="draggableTags" item-key="uid" :disabled="!editMode" :animation="200" ghost-class="ghost" @end="onTagDragEnd" class="contents">
                <template #item="{ element: tag, index: i }">
                    <input v-if="isEditing('tag', i)" class="inline-edit" v-model="editingItem!.value"
                        @keydown.enter="saveEdit" @keydown.esc="cancelEdit" @blur="saveEdit" autofocus />
                    <button v-else class="tag-button" :class="{ active: activeTag === tag.name }"
                        :style="{ '--tag-color': tagColor(tag.name) }" @click="handleTagClick(i, tag)">
                        <el-icon class="tag-icon"><RemarkTagIcon /></el-icon>
                        {{ tag.name }}<span v-if="editMode" class="del-btn" @click.stop="deleteTag(tag)">−</span>
                    </button>
                </template>
            </draggable>
            <template v-if="editMode">
                <input v-if="addingTag" class="inline-edit" v-model="newTagName"
                    placeholder="New tag" @keydown.enter="confirmAddTag" @keydown.esc="cancelAddTag" @blur="confirmAddTag" autofocus />
                <button v-else-if="draggableTags.length < 8" class="add-btn" @click.stop="addingTag = true">
                    <el-icon :size="18"><Add /></el-icon>
                </button>
            </template>
        </div>

        <draggable :list="draggableRemarks" item-key="uid" :disabled="!editMode" :animation="200" ghost-class="ghost" @end="onRemarkDragEnd" class="quick-buttons">
            <template #item="{ element: remark, index: i }">
                <input v-if="isEditing('remark', i)" class="inline-edit" v-model="editingItem!.value"
                    @keydown.enter="saveEdit" @keydown.esc="cancelEdit" @blur="saveEdit" autofocus />
                <button v-else class="quick-button" @click="handleRemarkClick(i, remark)">
                    {{ remark.name }}<span v-if="editMode" class="del-btn" @click.stop="deleteRemark(remark)">−</span>
                </button>
            </template>
        </draggable>
    </div>
</template>

<style lang="scss" scoped>
.contents { display: contents; }
.tag-button {
    font-size: 14px;
    cursor: pointer;
    background-color: #fff;
    color: #3A3A3A;
    border-radius: 10px;
    height: 50px;
    padding: 0 10px;
    border: 1px solid #A8A8A8;
    user-select: none;
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .tag-icon { font-size: 16px; color: var(--tag-color); flex-shrink: 0; margin: 0; }
    &:hover { border-color: #999; }
    &.active { background-color: #52599D; color: #fff; border-color: #52599D; }
}
.add-btn {
    cursor: pointer;
    background-color: #b0bec5;
    color: #fff;
    border-radius: 8px;
    padding: 2px 20px;
    border: none;
    user-select: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:hover { background-color: #90a4ae; }
}
.del-btn {
    position: absolute;
    top: -6px;
    right: 0px;
    background: #E53030;
    border-radius: 50%;
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
}
.inline-edit {
    font-size: 14px;
    border: 1px solid #52599D;
    border-radius: 8px;
    padding: 3px 8px;
    outline: none;
    width: 80px;
    color: #333;
}

.quick-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
}
.quick-button {
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #52599D;
    color: #fff;
    border-radius: 10px;
    padding: 5px 10px;
    border: none;
    user-select: none;
    position: relative;
    &:hover { background-color: #6369b3; transform: translateY(-1px); }
    &:active { transform: scale(0.95); }
}
// Edit mode: allow touch drag + let Sortable own the transform animation (button's own transition:all hijacks it)
.edit-mode .quick-button,
.edit-mode .tag-button { touch-action: none; transition: none; }
.ghost { opacity: 0.5; }
</style>
